import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, TrendingUp, Shield, Zap, Target } from 'lucide-react';

interface StrategyConfig {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  riskLevel: 'Low' | 'Medium' | 'High';
  targetReturn: string;
  parameters: {
    maxDrawdown: number;
    leverage: number;
    volatilityTarget: number;
  };
}

interface StrategyPerformance {
  strategyId: string;
  nav: number;
  dailyReturn: number;
  ytdReturn: number;
  sharpeRatio: number;
  winRate: number;
  maxDrawdown: number;
  totalTrades: number;
  benchmarkOutperformance: number;
}

const strategies: StrategyConfig[] = [
  {
    id: 'core',
    name: 'Core',
    description: 'Conservative long-term growth strategy with low volatility',
    icon: Shield,
    riskLevel: 'Low',
    targetReturn: '8-12%',
    parameters: {
      maxDrawdown: 5,
      leverage: 1.2,
      volatilityTarget: 10
    }
  },
  {
    id: 'tactical',
    name: 'Tactical',
    description: 'Active allocation strategy responding to market conditions',
    icon: Target,
    riskLevel: 'Medium',
    targetReturn: '12-18%',
    parameters: {
      maxDrawdown: 8,
      leverage: 1.5,
      volatilityTarget: 15
    }
  },
  {
    id: 'convex',
    name: 'Convex',
    description: 'Asymmetric risk strategy capturing upside while limiting downside',
    icon: TrendingUp,
    riskLevel: 'Medium',
    targetReturn: '15-25%',
    parameters: {
      maxDrawdown: 10,
      leverage: 2.0,
      volatilityTarget: 18
    }
  },
  {
    id: 'adaptive',
    name: 'Adaptive',
    description: 'Machine learning driven strategy adapting to market regimes',
    icon: Zap,
    riskLevel: 'High',
    targetReturn: '20-35%',
    parameters: {
      maxDrawdown: 15,
      leverage: 2.5,
      volatilityTarget: 22
    }
  }
];

const mockPerformanceData: Record<string, StrategyPerformance> = {
  core: {
    strategyId: 'core',
    nav: 1050000,
    dailyReturn: 0.45,
    ytdReturn: 8.2,
    sharpeRatio: 1.65,
    winRate: 72,
    maxDrawdown: -3.2,
    totalTrades: 145,
    benchmarkOutperformance: 2.1
  },
  tactical: {
    strategyId: 'tactical',
    nav: 1180000,
    dailyReturn: 1.25,
    ytdReturn: 14.8,
    sharpeRatio: 1.85,
    winRate: 68,
    maxDrawdown: -6.7,
    totalTrades: 287,
    benchmarkOutperformance: 4.3
  },
  convex: {
    strategyId: 'convex',
    nav: 1320000,
    dailyReturn: 2.15,
    ytdReturn: 22.4,
    sharpeRatio: 2.12,
    winRate: 65,
    maxDrawdown: -9.1,
    totalTrades: 198,
    benchmarkOutperformance: 7.8
  },
  adaptive: {
    strategyId: 'adaptive',
    nav: 1450000,
    dailyReturn: 2.85,
    ytdReturn: 28.7,
    sharpeRatio: 2.35,
    winRate: 62,
    maxDrawdown: -12.3,
    totalTrades: 342,
    benchmarkOutperformance: 11.2
  }
};

interface StrategySelectorProps {
  selectedStrategy: string;
  onStrategyChange: (strategyId: string) => void;
  system: 'AEX' | 'AEC';
  isLocked?: boolean;
}

const StrategySelector: React.FC<StrategySelectorProps> = ({ 
  selectedStrategy, 
  onStrategyChange, 
  system,
  isLocked = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentStrategy = strategies.find(s => s.id === selectedStrategy) || strategies[0];
  const performance = mockPerformanceData[selectedStrategy];

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'Low': return 'text-green-400';
      case 'Medium': return 'text-yellow-400';
      case 'High': return 'text-red-400';
      default: return 'text-slate-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Strategy Selection Dropdown */}
      <div className="glass-dark p-6 rounded-xl border border-yellow-500/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            {system} Strategy Configuration
          </h3>
          {isLocked && (
            <div className="flex items-center text-yellow-400 text-sm">
              <Shield className="w-4 h-4 mr-1" />
              Locked
            </div>
          )}
        </div>

        <div className="relative">
          <button
            onClick={() => !isLocked && setIsOpen(!isOpen)}
            disabled={isLocked}
            className={`w-full flex items-center justify-between p-4 rounded-lg border transition-all ${
              isLocked 
                ? 'bg-slate-700/50 border-slate-600 cursor-not-allowed opacity-75'
                : 'bg-slate-800/50 border-slate-600 hover:border-yellow-500/50 cursor-pointer'
            }`}
          >
            <div className="flex items-center">
              <currentStrategy.icon className="w-5 h-5 mr-3 text-yellow-400" />
              <div className="text-left">
                <div className="text-white font-medium">{currentStrategy.name}</div>
                <div className="text-sm text-slate-400 [text-shadow:_0_1px_2px_rgb(0_0_0_/_80%)]">{currentStrategy.description}</div>
              </div>
            </div>
            {!isLocked && (
              <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            )}
          </button>

          {isOpen && !isLocked && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full left-0 right-0 mt-2 bg-slate-800 border border-slate-600 rounded-lg shadow-xl z-50"
            >
              {strategies.map((strategy) => (
                <button
                  key={strategy.id}
                  onClick={() => {
                    onStrategyChange(strategy.id);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center p-4 hover:bg-slate-700/50 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                    strategy.id === selectedStrategy ? 'bg-yellow-500/10 border-l-4 border-yellow-500' : ''
                  }`}
                >
                  <strategy.icon className="w-5 h-5 mr-3 text-yellow-400" />
                  <div className="text-left flex-1">
                    <div className="text-white font-medium">{strategy.name}</div>
                    <div className="text-sm text-slate-400 [text-shadow:_0_1px_2px_rgb(0_0_0_/_80%)]">{strategy.description}</div>
                    <div className="flex items-center mt-1 space-x-4 text-xs">
                      <span className={`${getRiskColor(strategy.riskLevel)}`}>
                        Risk: {strategy.riskLevel}
                      </span>
                      <span className="text-slate-500">Target: {strategy.targetReturn}</span>
                    </div>
                  </div>
                </button>
              ))}
            </motion.div>
          )}
        </div>

        {/* Strategy Parameters */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-400">
              {currentStrategy.parameters.maxDrawdown}%
            </div>
            <div className="text-sm text-slate-400 [text-shadow:_0_1px_2px_rgb(0_0_0_/_80%)]">Max Drawdown</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-400">
              {currentStrategy.parameters.leverage}x
            </div>
            <div className="text-sm text-slate-400 [text-shadow:_0_1px_2px_rgb(0_0_0_/_80%)]">Leverage</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-400">
              {currentStrategy.parameters.volatilityTarget}%
            </div>
            <div className="text-sm text-slate-400 [text-shadow:_0_1px_2px_rgb(0_0_0_/_80%)]">Vol Target</div>
          </div>
        </div>
      </div>

      {/* Strategy Performance */}
      <div className="glass-dark p-6 rounded-xl border border-yellow-500/20">
        <h3 className="text-lg font-semibold text-white mb-4">
          {currentStrategy.name} Performance ({system})
        </h3>
        
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-xl font-bold text-white">
              ${performance.nav.toLocaleString()}
            </div>
            <div className="text-sm text-slate-400 [text-shadow:_0_1px_2px_rgb(0_0_0_/_80%)]">NAV</div>
          </div>
          <div className="text-center">
            <div className={`text-xl font-bold ${performance.ytdReturn >= 0 ? 'text-green-400' : 'text-red-400'} truncate`}>
              {performance.ytdReturn >= 0 ? '+' : ''}{performance.ytdReturn.toFixed(1)}%
            </div>
            <div className="text-sm text-slate-400 [text-shadow:_0_1px_2px_rgb(0_0_0_/_80%)]">YTD Return</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-yellow-400 truncate">
              {performance.sharpeRatio.toFixed(2)}
            </div>
            <div className="text-sm text-slate-400 [text-shadow:_0_1px_2px_rgb(0_0_0_/_80%)]">Sharpe Ratio</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-green-400 truncate">
              {performance.winRate}%
            </div>
            <div className="text-sm text-slate-400 [text-shadow:_0_1px_2px_rgb(0_0_0_/_80%)]">Win Rate</div>
          </div>
        </div>

        {/* Benchmark Comparison */}
        <div className="mt-6 p-4 bg-slate-800/50 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400 [text-shadow:_0_1px_2px_rgb(0_0_0_/_80%)]">vs Benchmark</span>
            <span className={`text-lg font-bold ${performance.benchmarkOutperformance >= 0 ? 'text-green-400' : 'text-red-400'} truncate`}>
              {performance.benchmarkOutperformance >= 0 ? '+' : ''}{performance.benchmarkOutperformance.toFixed(1)}%
            </span>
          </div>
          <div className="mt-2 h-2 bg-slate-700 rounded-full">
            <div 
              className="h-2 bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-1000"
              style={{ width: `${Math.min(Math.max((performance.benchmarkOutperformance + 5) / 15 * 100, 0), 100)}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategySelector;
